import React, { useState, useMemo } from "react";

import List from '@mui/material/List';
import Stack from "@mui/material/Stack";
import Radio from "@mui/material/Radio";
import FormHeading from "./FormHeading";
import { useDispatch, useSelector } from "react-redux";
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import { isChrome, isAndroid, isSafari, isIOS13, isIPad13, isIPhone13, isIPod13, isIOS } from 'react-device-detect';

import ACHForm from "./paymentOptionComponents/ACHForm";
import CheckCash from "./paymentOptionComponents/CheckCash";
import StripeForm from "./paymentOptionComponents/StripeForm";
import BankTransfer from "./paymentOptionComponents/BankTransfer";
import { CheckboxWithLabel, ValidationError } from "../../../components";
import { useNonInitialEffect } from "../../../hooks/UseNonInitialEffect";
import { APPLE, BANK, CHEQUE, GOOGLE, paymentOptions as constantOptions, STRIPE, ACH, PAYPAL, VENMO } from "../../../constants";
import { getACHPaymentIntent } from "../../../helpers/PaymentHandlers";
import { errorActions } from "../../../store/actions";
import { validateCheckout } from "../../../validations";
import { Box } from "@mui/material";

function PaymentsSection({ state, handleChange, stripeReference, ACHReference }) {
    const dispatch = useDispatch();

    const [paymentOptions, setPaymentOptions] = useState(constantOptions);

    const validationErrors = useSelector(state => state.validationErrors)
    const fetchedPaymentMethods = useSelector(state => state.payment_methods.payment_methods);
    const countryAllowedForCheque = useSelector(state => state.authentication.allowed_country_for_cheque);

    const handleListClick = (event, value) => {
        event.target = {
            ...event.target,
            name: "selectedPaymentMethod",
            value: value.id
        };

        handleChange(event)
    }

    const displayOption = (method) => {
        switch (method.id) {
            case CHEQUE:
                return !state.isRecurring
                    && state.donationAmount >= method.minDonation
                    && countryAllowedForCheque
            case BANK:
                // case ACH:
                return !state.isRecurring

            case VENMO:
                return !state.isRecurring

            default:
                return true
        }
    }

    const getMethodComponent = (methodName) => {
        switch (methodName) {
            case BANK:
                return <BankTransfer state={state} handleChange={handleChange} validationErrors={validationErrors} />

            case CHEQUE:
                return <CheckCash state={state} handleChange={handleChange} validationErrors={validationErrors} />

            case STRIPE:
                return <StripeForm state={state} ref={stripeReference} handleChange={handleChange} validationErrors={validationErrors} />

            case ACH:
                return <ACHForm ref={ACHReference} state={state} />

            default:
                break;
        }
    }

    const updateOptionValues = (optionsArray, index, newValue) => {
        optionsArray[index].render = true;
        optionsArray[index].minDonation = newValue.minimum_donation_amount;
        optionsArray[index].transactionFee = newValue.transaction_fee_percentage;
    }

    useNonInitialEffect(() => {
        let updatedOptions = [...paymentOptions];
        updatedOptions.map(option => option.render = false);

        for (const paymentMethod of fetchedPaymentMethods) {

            const optionIndex = updatedOptions.findIndex(option => option.id == paymentMethod.name);

            if (optionIndex >= 0)
                updateOptionValues(updatedOptions, optionIndex, paymentMethod)

            if (paymentMethod.allow_google_apple_pay) {

                let googleAppleIndex = 0;

                if (isSafari || isIOS13 || isIPad13 || isIPhone13 || isIPod13 || isIOS)
                    googleAppleIndex = updatedOptions.findIndex(option => option.id == APPLE)

                else if (isChrome || isAndroid)
                    googleAppleIndex = updatedOptions.findIndex(option => option.id == GOOGLE)


                if (googleAppleIndex >= 0) updateOptionValues(updatedOptions, googleAppleIndex, paymentMethod)
            }

            if (paymentMethod.allow_ach) {
                const achIndex = updatedOptions.findIndex(option => option.id == ACH);

                if (achIndex >= 0) updateOptionValues(updatedOptions, achIndex, paymentMethod)
            }

            if (paymentMethod.name === PAYPAL && paymentMethod.allow_venmo) {
                const venmoIndex = updatedOptions.findIndex(option => option.id == VENMO);

                if (venmoIndex >= 0) {
                    updateOptionValues(updatedOptions, venmoIndex, paymentMethod);
                    handleChange({ target: { name: 'paypalPublicKey', value: paymentMethod.public_key } });
                }
            }

            if (paymentMethod.name == STRIPE)
                handleChange({ target: { name: 'stripePublicKey', value: paymentMethod.public_key } });

        }

        setPaymentOptions(updatedOptions)

    }, [fetchedPaymentMethods])

    const mapPaymentOptions = () => {
        return <List className="PaymentOptions">
            {paymentOptions.map((method) => {
                return method.render && displayOption(method) ? (
                    <React.Fragment key={method.id}>
                        <ListItem
                            className={state.selectedPaymentMethod == method.id ? 'ActiveOption' : 'InactiveOption'}
                            onClick={e => handleListClick(e, method)}
                            disablePadding
                        >
                            <ListItemButton role={undefined} dense disableRipple disableTouchRipple>
                                <ListItemIcon className="ListRadio">
                                    <Radio
                                        name="selectedPaymentMethod"
                                        value={method.id}
                                        checked={state.selectedPaymentMethod == method.id}
                                        onChange={handleChange}
                                    />
                                </ListItemIcon>
                                   <Box className="PaymentMethodIcon">
                                        {method.icon}
                                   </Box>
                                <ListItemText className={`PaymentMethodName1 ${state.selectedPaymentMethod == method.id && "PaymentBold"}`} id={`payment-method-${method.id}`} primary={method.name} />
                            </ListItemButton>
                              {
                                state.selectedPaymentMethod == method.id ? getMethodComponent(method.id) : null
                             }
                        </ListItem>
                    </React.Fragment>
                ) : null
            })}
        </List>
    }

    useNonInitialEffect(() => {
        if (state.selectedPaymentMethod && state.selectedPaymentMethod !== ACH) {
            const selectedMethodTransactionFee = paymentOptions.find(option => option.id == state.selectedPaymentMethod).transactionFee;

            handleChange({ target: { name: 'transactionFeePercentage', value: selectedMethodTransactionFee } });
        }

        if (state.selectedPaymentMethod == ACH) {
            dispatch(errorActions.setValidationErrors(validateCheckout(state)))

            getACHPaymentIntent(state)
                .then(intent => {
                    ACHReference.current.collectBankCardForPayment(intent.client_secret);
                    if (state.isRecurring) handleChange({
                        target: {
                            name: 'achRecurringDetails', value: {
                                start_date: intent.start_date,
                                end_date: intent.end_date,
                                invoice_id: intent.invoice_id,
                                stripe_sub_id: intent.stripe_sub_id,
                            }
                        }
                    });
                })
                .catch(e => console.log(e.message))
        }

        if (state.selectedPaymentMethod == VENMO) {
            dispatch(errorActions.setValidationErrors(validateCheckout(state)))
        }

    }, [state.selectedPaymentMethod])

	return (
		<Stack id="paymentSection" sx={{ marginBottom: '25px' }}>
			<FormHeading heading="Choose your payment option" description={"Select the payment method you would like to use to complete your donation"}/>
			{mapPaymentOptions()}
			{state.transactionFeePercentage && state.selectedPaymentMethod !== ACH ? (
				<Box sx={{ marginTop: '25px' }}>
					<CheckboxWithLabel
						nameAndId="coverTheTransactionFee"
						label="I want to cover the fees associated with my donation so more of my donation goes directly to the cause."
						handleCheck={handleChange}
						// className="CoverTransactionFeeSwitch"
						// checked={state.coverTheTransactionFee}
					/>
				</Box>
			) : null}
			{validationErrors.selectedPaymentMethod && <ValidationError message={validationErrors.selectedPaymentMethod} />}
		</Stack>
	);
}

export default function ({ handleChange, validationErrors, stripeReference, ACHReference }) {
    const state = useSelector(state => state.donationCheckout);

    return useMemo(() => {
        return <PaymentsSection ACHReference={ACHReference} stripeReference={stripeReference} state={state} handleChange={handleChange} validationErrors={validationErrors} />
    }, [ACHReference,
        stripeReference,
        state.selectedPaymentMethod,
        state.selectedBank,
        state.bankSlip,
        validationErrors.selectedPaymentMethod,
        state.selectedPaymentMethodDetails,
        validationErrors.paymentMethodDetails,
        state.isRecurring,
        state.donationAmount,
        state.coverTheTransactionFee,
        state.stripePublicKey,
        state.transactionFeePercentage,])
}